import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ColumnsType } from "@similarweb/pro-logic-components";

import { compareLocaleStrings } from "app/pages/sales-intelligence/utils/sort";

import type { AssignedAccountUser, TeamListItem } from "../../types/teams.types";
import { teamMemberCountryLabel } from "../../utils/teamMemberCountryLabel";
import { getAccountUserColumnsMap } from "../TeamAccountUsersTable/accountUserColumns";
import { TeamAccountUsersTable } from "../TeamAccountUsersTable/TeamAccountUsersTable";

import { AssignedUsersTableTeamNamesCell } from "./AssignedUsersTableTeamNamesCell";

const formatTeamNames = (teams: AssignedAccountUser["teams"]): string =>
    teams
        .map((team) => team.name ?? "")
        .filter((name) => name.length > 0)
        .join(", ");

type AssignedUsersTableProps = {
    rows: AssignedAccountUser[];
    teams: readonly TeamListItem[];
};

export const AssignedUsersTable = ({ rows, teams }: AssignedUsersTableProps) => {
    const { translate } = useTranslate();

    const columnConfig = useMemo((): ColumnsType<AssignedAccountUser> => {
        const { name, email, jobTitle, country } = getAccountUserColumnsMap<AssignedAccountUser>(translate);

        return [
            name,
            email,
            {
                dataIndex: "teams",
                title: translate("si.management.teams.users.column.team_name"),
                sorter: (a, b, sortOrder) =>
                    compareLocaleStrings(formatTeamNames(a.teams), formatTeamNames(b.teams), sortOrder),
                width: 280,
                render: (_value, record) => <AssignedUsersTableTeamNamesCell teams={record.teams} />,
            },
            jobTitle,
            country,
        ];
    }, [translate]);

    const getSearchableValues = useMemo(
        () => (row: AssignedAccountUser) =>
            [
                row.name,
                row.email,
                row.jobTitle,
                teamMemberCountryLabel(row.country),
                ...row.teams.map((team) => team.name),
            ] as const,
        [],
    );

    return (
        <TeamAccountUsersTable
            rows={rows}
            teams={teams}
            columnConfig={columnConfig}
            searchPlaceholderKey="si.management.teams.detail.search.placeholder"
            selectionTextKey="si.management.teams.detail.selected_count"
            dataAutomation="teams-management-assigned-users-table"
            getSearchableValues={getSearchableValues}
            assignClickSource="assigned_bulk"
        />
    );
};
